<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Appointments"
      subtitle="Manage student appointment requests"
      icon="fas fa-calendar-check"
      gradient="green"
    />

    <!-- View Toggle -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <BButtonGroup size="sm">
        <BButton
          :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
          @click="viewMode = 'table'"
        >
          <i class="fas fa-table me-1" />
          Table View
        </BButton>
        <BButton
          :variant="viewMode === 'calendar' ? 'primary' : 'outline-primary'"
          @click="viewMode = 'calendar'; fetchCalendarSlots()"
        >
          <i class="fas fa-calendar-alt me-1" />
          Calendar View
        </BButton>
      </BButtonGroup>
    </div>

    <!-- TABLE VIEW -->
    <template v-if="viewMode === 'table'">
      <BRow class="py-2">
        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Search"
            label-for="filterSearch"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              id="filterSearch"
              v-model="tableAppointments.filter.search"
              placeholder="search by reference, name, email..."
              autocomplete="off"
              debounce="1000"
              type="text"
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="3"
        >
          <BFormGroup
            label="Status"
            label-for="filterStatus"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <VSelect
              v-model="tableAppointments.selected.status"
              placeholder="filter status"
              input-id="filterStatus"
              label="name"
              clearable
              append-to-body
              :options="statusOptions"
              :disabled="tableAppointments.busy"
              :calculate-position="calculateDropPosition"
            >
              <template #option="{ name }">
                <div
                  class="text-break text-wrap"
                  v-text="name"
                />
              </template>
              <template #no-options="">
                no available status
              </template>
            </VSelect>
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="3"
        >
          <BFormGroup
            label="Concern Type"
            label-for="filterConcernType"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <VSelect
              v-model="tableAppointments.selected.concernType"
              placeholder="filter concern type"
              input-id="filterConcernType"
              label="name"
              clearable
              append-to-body
              :options="tableAppointments.options.concernTypes"
              :loading="tableAppointments.fetching.concernTypes"
              :disabled="tableAppointments.busy || tableAppointments.fetching.concernTypes"
              :calculate-position="calculateDropPosition"
            >
              <template #option="{ name }">
                <div
                  class="text-break text-wrap"
                  v-text="name"
                />
              </template>
              <template #no-options="">
                no available concern type
              </template>
            </VSelect>
          </BFormGroup>
        </BCol>
      </BRow>

      <CustomTable
        ref="tableAppointments"
        :provider="tableProvider"
        :fields="tableAppointments.fields"
        :filter="tableAppointments.filter"
        :filter-on="tableAppointments.filterOn"
        :sort-by="tableAppointments.sortBy"
        :sort-desc="tableAppointments.sortDesc"
        :sort-direction="tableAppointments.sortDirection"
        :total-rows="tableAppointments.totalRows"
        :current-page="tableAppointments.currentPage"
        :per-page="tableAppointments.perPage"
        :stacked="tableAppointments.stacked"
        :busy="tableAppointments.busy"
      >
        <template #cell(action)="{ item }">
          <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
            <BButton
              size="sm"
              variant="info"
              :disabled="tableAppointments.busy"
              @click.prevent="viewAppointment(item)"
            >
              View
            </BButton>
            <BButton
              size="sm"
              variant="danger"
              :disabled="tableAppointments.busy"
              @click.prevent="deleteAppointmentConfirm(item)"
            >
              Delete
            </BButton>
          </div>
        </template>

        <template #cell(status)="{ item }">
          <BBadge :variant="getStatusVariant(item.status)">
            {{ formatStatus(item.status) }}
          </BBadge>
        </template>

        <template #cell(concern_type)="{ item }">
          {{ item.concern_type ? item.concern_type.name : 'Other' }}
        </template>

        <template #cell(slot)="{ item }">
          <span v-if="item.slot">
            {{ dateFormatter(item.slot.date) }} {{ formatTime(item.slot.start_time) }} - {{ formatTime(item.slot.end_time) }}
          </span>
          <span v-else>N/A</span>
        </template>

        <template #cell(is_walk_in)="{ item }">
          <BBadge :variant="item.is_walk_in ? 'warning' : 'info'">
            {{ item.is_walk_in ? 'Walk-in' : 'Online' }}
          </BBadge>
        </template>
      </CustomTable>
    </template>

    <!-- CALENDAR VIEW -->
    <template v-if="viewMode === 'calendar'">
      <BCard class="border shadow-sm">
        <BOverlay
          :show="calendarState.loading"
          opacity="0.5"
        >
          <SlotCalendar
            :slots="calendarAppointmentItems"
            :selected-slot-id="calendarState.selectedSlotId"
            :show-slots-inline="true"
            :initial-date="calendarInitialDate"
            @month-change="onCalendarMonthChange"
            @date-select="onCalendarDateSelect"
            @slot-click="onCalendarSlotClick"
          />
        </BOverlay>

        <!-- Selected Day Detail -->
        <div
          v-if="calendarState.selectedDate"
          class="mt-4 pt-4 border-top"
        >
          <h6 class="font-weight-bold mb-3">
            <i class="fas fa-calendar-day me-2" />
            {{ formatCalendarDate(calendarState.selectedDate) }}
          </h6>

          <div
            v-if="selectedDateSlots.length === 0"
            class="text-center py-4 text-muted"
          >
            <i
              class="fas fa-calendar-times mb-2"
              style="font-size: 2rem;"
            />
            <p class="mb-0">
              No slots for this date
            </p>
          </div>

          <!-- Slots with Appointments -->
          <div
            v-for="slot in selectedDateSlots"
            :key="slot.id"
            class="slot-appointments-card mb-3"
          >
            <div class="slot-appointments-header">
              <span class="slot-time">
                <i class="fas fa-clock me-2" />
                {{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}
              </span>
              <BBadge :variant="slot.appointments.length ? 'success' : 'secondary'">
                {{ slot.appointments.length }} appointment{{ slot.appointments.length !== 1 ? 's' : '' }}
              </BBadge>
            </div>

            <div
              v-if="slot.appointments.length === 0"
              class="slot-empty"
            >
              No appointments in this slot
            </div>

            <div
              v-else
              class="slot-appointments-list"
            >
              <div
                v-for="apt in slot.appointments"
                :key="apt.id"
                class="appointment-item"
                @click="viewAppointment(apt)"
              >
                <div class="appointment-info">
                  <span class="appointment-ref">{{ apt.reference_number }}</span>
                  <span class="appointment-name">{{ apt.full_name }}</span>
                  <BBadge
                    :variant="apt.is_walk_in ? 'warning' : 'info'"
                    class="ms-2"
                  >
                    {{ apt.is_walk_in ? 'Walk-in' : 'Online' }}
                  </BBadge>
                </div>
                <div class="appointment-status">
                  <BBadge :variant="getStatusVariant(apt.status)">
                    {{ formatStatus(apt.status) }}
                  </BBadge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BCard>
    </template>

    <!-- View/Status Update Modal -->
    <BModal
      ref="modalAppointment"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Appointment Details"
    >
      <BRow>
        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Reference Number"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.reference_number"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Status"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <VSelect
              v-model="selectedStatus"
              placeholder="select status"
              label="name"
              :options="statusOptions"
              :disabled="state.busy"
              :calculate-position="calculateDropPosition"
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Full Name"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.full_name"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Email"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.email"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Contact Number"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.contact_number || 'N/A'"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Type"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.is_walk_in ? 'Walk-in' : 'Online'"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Concern Type"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.concern_type ? formAppointment.concern_type.name : 'Other'"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Appointment Slot"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              :value="formAppointment.slot ? `${dateFormatter(formAppointment.slot.date)} ${formatTime(formAppointment.slot.start_time)} - ${formatTime(formAppointment.slot.end_time)}` : 'N/A'"
              disabled
            />
          </BFormGroup>
        </BCol>

        <BCol cols="12">
          <BFormGroup
            label="Description"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormTextarea
              :value="formAppointment.description || 'N/A'"
              rows="3"
              disabled
            />
          </BFormGroup>
        </BCol>
      </BRow>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="success"
          :disabled="state.busy"
          @click.prevent="updateStatus"
        >
          Update Status
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import SlotCalendar from '@/components/custom/SlotCalendar.vue'
import { SAdminAppointment, SAdminAppointmentSlot, SSharedList } from '@/services'

export default {
  name: 'VAdminAppointments',

  components: {
    AdminPageHeader,
    SlotCalendar
  },

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Appointments'
  }),

  data () {
    return {
      viewMode: 'table',
      state: {
        busy: false
      },
      selectedStatus: null,
      formAppointment: {
        id: '',
        reference_number: '',
        is_walk_in: false,
        student_id: '',
        full_name: '',
        email: '',
        contact_number: '',
        student_id_number: '',
        slot_id: '',
        concern_type_id: '',
        description: '',
        status: '',
        slot: null,
        concern_type: null
      },
      statusOptions: [
        { id: 'for_confirmation', name: 'For Confirmation' },
        { id: 'scheduled', name: 'Scheduled' },
        { id: 'completed', name: 'Completed' },
        { id: 'canceled', name: 'Canceled' }
      ],
      tableAppointments: {
        busy: false,
        fetching: {
          concernTypes: false
        },
        options: {
          concernTypes: []
        },
        selected: {
          status: null,
          concernType: null
        },
        filter: {
          search: '',
          status: '',
          concern_type_id: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'reference_number', label: 'Reference #', class: 'min-w-130px', sortable: true },
          { mobile: 2, key: 'full_name', label: 'Name', class: 'min-w-150px' },
          { mobile: 3, key: 'concern_type', label: 'Concern', class: 'min-w-120px' },
          { mobile: 4, key: 'slot', label: 'Slot', class: 'min-w-175px' },
          { mobile: 5, key: 'is_walk_in', label: 'Type', class: 'text-center min-w-80px' },
          { mobile: 6, key: 'status', class: 'text-center min-w-120px' },
          { mobile: 7, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      },
      // Calendar view state
      calendarInitialDate: new Date(),
      calendarState: {
        loading: false,
        currentYear: new Date().getFullYear(),
        currentMonth: new Date().getMonth(),
        selectedDate: null,
        selectedSlotId: null,
        slots: [],
        appointments: []
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    calendarAppointmentItems () {
      return this.calendarState.appointments
        .map(appointment => this.normalizeAppointment(appointment))
        .filter(appointment => this.normalizeDateKey(appointment.slot?.date))
        .map(appointment => ({
          id: `appointment-${appointment.id}`,
          date: this.normalizeDateKey(appointment.slot.date),
          start_time: appointment.slot.start_time,
          end_time: appointment.slot.end_time,
          is_available: true,
          active_appointments_count: 0,
          inline_label: appointment.reference_number,
          appointment
        }))
    },

    selectedDateSlots () {
      if (!this.calendarState.selectedDate) return []
      const selectedDateKey = this.normalizeDateKey(this.calendarState.selectedDate)
      const slotMap = {}

      this.calendarState.appointments
        .map(appointment => this.normalizeAppointment(appointment))
        .forEach(apt => {
          const slot = apt.slot
          if (!slot) return

          const slotDateKey = this.normalizeDateKey(slot.date)
          if (slotDateKey !== selectedDateKey) return

          const slotKey = slot.id || `${slotDateKey}-${slot.start_time || ''}-${slot.end_time || ''}`
          if (!slotMap[slotKey]) {
            slotMap[slotKey] = {
              id: slotKey,
              start_time: slot.start_time,
              end_time: slot.end_time,
              appointments: []
            }
          }

          slotMap[slotKey].appointments.push(apt)
        })

      return Object.values(slotMap).sort((a, b) =>
        (a.start_time || '').localeCompare(b.start_time || '')
      )
    }
  },

  watch: {
    'tableAppointments.selected.status' (status) {
      this.tableAppointments.filter.status = status?.id || ''
    },
    'tableAppointments.selected.concernType' (type) {
      this.tableAppointments.filter.concern_type_id = type?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getConcernTypes()
    }
  },

  methods: {
    buildStudentFullName (student) {
      if (!student || typeof student !== 'object') return ''
      return [student.first_name, student.middle_name, student.last_name, student.extension_name]
        .filter(Boolean)
        .join(' ')
        .trim()
    },

    getStatusVariant (status) {
      const variants = {
        for_confirmation: 'warning',
        scheduled: 'info',
        completed: 'success',
        canceled: 'secondary'
      }
      return variants[status] || 'secondary'
    },

    formatStatus (status) {
      const labels = {
        for_confirmation: 'For Confirmation',
        scheduled: 'Scheduled',
        completed: 'Completed',
        canceled: 'Canceled'
      }
      return labels[status] || status || 'N/A'
    },

    async getConcernTypes () {
      this.tableAppointments.fetching.concernTypes = true
      return SSharedList.getActiveAppointmentConcernTypes({ per_page: 500, sort: 'name', sort_desc: false })
        .then(({ data }) => {
          this.tableAppointments.options.concernTypes = data.data || []
        })
        .catch(() => {
          this.tableAppointments.options.concernTypes = []
        })
        .finally(() => {
          this.tableAppointments.fetching.concernTypes = false
        })
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableAppointments.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        status: filter.status,
        concern_type_id: filter.concern_type_id
      }

      return await SAdminAppointment.getList(payload).then(
        ({ data }) => {
          this.tableAppointments.totalRows = data.total_rows
          return (data.items || []).map(item => this.normalizeAppointment(item))
        }
      ).catch(() => {
        this.tableAppointments.totalRows = 0
        return []
      }).finally(() => {
        this.tableAppointments.busy = false
      })
    },

    viewAppointment (appointment) {
      this.formAppointment = { ...this.normalizeAppointment(appointment) }
      this.selectedStatus = this.statusOptions.find(s => s.id === appointment.status) || null
      this.$refs.modalAppointment.show()
    },

    async updateStatus () {
      if (!this.selectedStatus) {
        return this.swalInvalid('Please select a status')
      }

      this.swalConfirm({
        html: '<h6>Update appointment status?</h6>',
        confirmButtonText: 'Update',
        preConfirm: async () => {
          this.state.busy = true
          return SAdminAppointment.updateStatus(this.formAppointment.id, {
            status: this.selectedStatus.id
          }).then(
            ({ data: { message } }) => {
              this.swalSuccess(message).then(() => {
                this.$refs.modalAppointment.hide()
                this.$refs.tableAppointments.refetch()
              })
            }
          ).catch(
            ({ message }) => {
              this.swalError(message || 'Failed to update status')
            }
          ).finally(() => {
            this.state.busy = false
          })
        }
      })
    },

    async deleteAppointmentConfirm (appointment) {
      const result = await this.$swal({
        title: 'Delete Appointment',
        text: `Are you sure you want to delete appointment ${appointment.reference_number}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.deleteAppointment(appointment.id)
      }
    },

    async deleteAppointment (id) {
      this.state.busy = true
      try {
        await SAdminAppointment.destroy(id)
        this.swalSuccess('Appointment deleted successfully')
        this.$refs.tableAppointments.refetch()
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to delete appointment'
        this.swalError(message)
      } finally {
        this.state.busy = false
      }
    },

    // Calendar view methods
    toDateString (d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    normalizeDateKey (value) {
      if (!value) return ''
      if (typeof value === 'string' && value.length >= 10) {
        return value.slice(0, 10)
      }

      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return this.toDateString(d)
    },

    formatTime (time) {
      if (!time) return ''
      const [hours, minutes] = time.substring(0, 5).split(':')
      const h = parseInt(hours, 10)
      const suffix = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${h12}:${minutes} ${suffix}`
    },

    formatCalendarDate (dateStr) {
      const dateKey = this.normalizeDateKey(dateStr)
      const d = new Date(`${dateKey}T00:00:00`)
      return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    },

    prevCalendarMonth () {
      if (this.calendarState.currentMonth === 0) {
        this.calendarState.currentMonth = 11
        this.calendarState.currentYear--
      } else {
        this.calendarState.currentMonth--
      }
      this.calendarState.selectedDate = null
      this.fetchCalendarSlots()
    },

    nextCalendarMonth () {
      if (this.calendarState.currentMonth === 11) {
        this.calendarState.currentMonth = 0
        this.calendarState.currentYear++
      } else {
        this.calendarState.currentMonth++
      }
      this.calendarState.selectedDate = null
      this.fetchCalendarSlots()
    },

    selectCalendarDate (cell) {
      if (!cell.isCurrentMonth) return
      this.calendarState.selectedDate = cell.date
    },

    onCalendarDateSelect (date) {
      this.calendarState.selectedDate = date
    },

    onCalendarSlotClick (slot) {
      this.calendarState.selectedDate = slot.date
      this.calendarState.selectedSlotId = slot.id

      if (slot.appointment) {
        this.viewAppointment(slot.appointment)
      }
    },

    onCalendarMonthChange ({ year, month, dateFrom, dateTo }) {
      this.calendarState.currentYear = year
      this.calendarState.currentMonth = month
      this.calendarState.selectedDate = null
      this.calendarState.selectedSlotId = null
      this.fetchCalendarSlots({ dateFrom, dateTo })
    },

    async fetchCalendarSlots (range = null) {
      this.calendarState.loading = true
      const { currentYear, currentMonth } = this.calendarState
      const dateFrom = range?.dateFrom || this.toDateString(new Date(currentYear, currentMonth, 1))
      const dateTo = range?.dateTo || this.toDateString(new Date(currentYear, currentMonth + 1, 0))

      try {
        // Fetch slots for the month
        const slotsRes = await SAdminAppointmentSlot.getCalendar({
          date_from: dateFrom,
          date_to: dateTo,
          per_page: 500
        })
        this.calendarState.slots = slotsRes.data.items || []

        // Fetch appointments for the month
        const appointmentsRes = await SAdminAppointment.getList({
          date_from: dateFrom,
          date_to: dateTo,
          per_page: 500
        })
        this.calendarState.appointments = (appointmentsRes.data.items || []).map(item => this.normalizeAppointment(item))
      } catch {
        this.calendarState.slots = []
        this.calendarState.appointments = []
      } finally {
        this.calendarState.loading = false
      }
    },

    normalizeAppointment (appointment) {
      if (!appointment) return appointment

      const slot = appointment.slot || appointment.appointment_slot || appointment.appointmentSlot || null
      const concernType = appointment.concern_type || appointment.appointment_concern_type || appointment.appointmentConcernType || null
      const student = appointment.student || null

      const resolvedFullName = appointment.full_name || this.buildStudentFullName(student) || 'N/A'
      const resolvedEmail = appointment.email || student?.email || 'N/A'
      const resolvedContact = appointment.contact_number || student?.contact_number || ''
      const resolvedStudentIdNumber = appointment.student_id_number || student?.id_number || ''

      return {
        ...appointment,
        full_name: resolvedFullName,
        email: resolvedEmail,
        contact_number: resolvedContact,
        student_id_number: resolvedStudentIdNumber,
        slot,
        concern_type: concernType
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.calendar-weekday {
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  color: #64748b;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-cell {
  min-height: 110px;
  padding: 8px;
  border-right: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.15s ease;

  &:nth-child(7n) {
    border-right: none;
  }

  &:hover {
    background: #f8fafc;
  }

  &--other-month {
    background: #f8fafc;
    opacity: 0.5;
    cursor: default;

    &:hover {
      background: #f8fafc;
    }
  }

  &--today {
    background: #eef8ff;

    .calendar-day-number {
      background: var(--iq-primary-dark, #006398);
      color: #fff;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &--selected {
    background: #e6f7d6;
    border-color: var(--iq-secondary, #58dc00);
  }

  &--has-slots {
    background: #fff7e8;
  }
}

.calendar-day-number {
  font-weight: 600;
  font-size: 0.9rem;
  color: #334155;
  margin-bottom: 4px;
}

.calendar-indicators {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.calendar-count {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  background: #e6f7d6;
  color: #245a00;
}

.calendar-appointment-inline {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.calendar-appointment-chip {
  border: 0;
  border-radius: 4px;
  padding: 2px 5px;
  font-size: 0.62rem;
  line-height: 1.2;
  text-align: left;
  color: #fff;
  background: var(--iq-primary, #00a5fe);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-appointment-chip:hover {
  background: var(--iq-primary-dark, #006398);
}

.calendar-appointment-more {
  font-size: 0.62rem;
  color: #245a00;
  font-weight: 600;
}

.slot-appointments-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.slot-appointments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.slot-time {
  font-weight: 600;
  color: #1e293b;
}

.slot-empty {
  padding: 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
}

.slot-appointments-list {
  padding: 8px;
}

.appointment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.appointment-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.appointment-ref {
  font-weight: 600;
  color: var(--iq-primary-dark, #006398);
  font-size: 0.9rem;
}

.appointment-name {
  color: #475569;
  font-size: 0.9rem;
}
</style>
